import { useCallback, useEffect, useRef, useState } from "react";
import { listWorkspaceAgents, listPendingInvitations, listWorkspaceRoles } from "@/api/directoryApi";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { isTimeoutError } from "@/lib/fetchWithTimeout";
import { subscribeAgentStatusChanged } from "@/lib/agentPresenceEvents";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const PRESENCE_POLL_MS = 10_000;

function emailsMatch(a, b) {
  return String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
}

function patchAgentPresence(rows, { agentEmail, isOnline, lastLogin, updatedAt }) {
  const em = String(agentEmail || "").trim().toLowerCase();
  if (!em) return rows;
  let changed = false;
  const next = rows.map((r) => {
    if (r._type !== "agent") return r;
    if (!emailsMatch(r.email, em)) return r;
    const online = Boolean(isOnline);
    const login = lastLogin !== undefined ? lastLogin : r.lastLogin;
    const updated = updatedAt !== undefined ? updatedAt : r.updatedAt;
    if (Boolean(r.isOnline) === online && r.lastLogin === login && r.updatedAt === updated) {
      return r;
    }
    changed = true;
    return { ...r, isOnline: online, lastLogin: login, updatedAt: updated };
  });
  return changed ? next : rows;
}

// Transient failures we recover from silently (keep loading / last-good rows —
// never paint timeout or RBAC banners on this page):
//  - 401/403: workspace/roles context or Kong introspection not ready yet
//  - timeout / network: Kong, auth-service, or upstream blip
const AUTH_RETRY_DELAYS_MS = [500, 1200, 2500, 4000];

function isSilentDirectoryBlip(reason) {
  if (isTimeoutError(reason)) return true;
  if (reason && typeof reason === "object" && reason.transient === true) return true;
  const status = reason && typeof reason === "object" ? Number(reason.status) : NaN;
  if (status === 401 || status === 403) return true;
  // Network / TypeError / unknown (no HTTP status) — treat as transient.
  if (Number.isNaN(status) && reason instanceof Error) return true;
  return false;
}

/**
 * Merged directory for the active workspace.
 *
 * On the **default** workspace, lists agents and pending invites across the
 * entire tenant so admins can revoke access from all enrolled workspaces.
 *
 * Fetches accepted agents (`human_agent_accounts`) and pending/expired invitations
 * (`agent_invitations`) in parallel and merges them into a single sorted list.
 *
 * Row shapes:
 *  - Accepted agent:   `{ ...agentFields, _type: "agent" }`
 *  - Pending invite:   `{ email, role, status, expiresAt, createdAt, invitedByEmail, workspaceId?, _type: "pending" }`
 *
 * A failed invitation fetch does NOT break the agents list. Transient auth /
 * timeout blips are retried silently and never surfaced as banners.
 */
export function useWorkspaceDirectory() {
  const { activeWorkspaceId, activeWorkspace } = useWorkspace();
  const tenantWide = Boolean(activeWorkspace?.isDefault);
  const directoryScope = tenantWide ? "tenant" : "workspace";
  const [rows, setRows] = useState(/** @type {unknown[]} */ ([]));
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(/** @type {Error | null} */ (null));
  const [inviteError, setInviteError] = useState(/** @type {Error | null} */ (null));
  const [roleError, setRoleError] = useState(/** @type {Error | null} */ (null));
  const [roleOptions, setRoleOptions] = useState(/** @type {{ value: string, label: string, isSystem?: boolean }[]} */ ([]));
  // Monotonic token so a newer refresh / workspace switch supersedes in-flight retries.
  const seqRef = useRef(0);

  const refresh = useCallback(async () => {
    // Wait for workspace scope before firing (avoids a null/stale X-Workspace-Id).
    if (!activeWorkspaceId) {
      setLoading(false);
      return;
    }
    const seq = (seqRef.current += 1);
    setLoading(true);
    setError(null);
    setInviteError(null);
    setRoleError(null);

    let attempt = 0;
    for (;;) {
      const [agentsResult, invitesResult, rolesResult] = await Promise.allSettled([
        listWorkspaceAgents({ limit: 100, offset: 0, scope: directoryScope }),
        listPendingInvitations({ scope: directoryScope }),
        listWorkspaceRoles(),
      ]);
      if (seq !== seqRef.current) return; // superseded by a newer load

      const results = [agentsResult, invitesResult, rolesResult];
      const silentBlip = results.some(
        (r) => r.status === "rejected" && isSilentDirectoryBlip(r.reason)
      );
      if (silentBlip && attempt < AUTH_RETRY_DELAYS_MS.length) {
        if (typeof console !== "undefined" && console.debug) {
          console.debug("[users] transient directory blip — retrying silently");
        }
        await delay(AUTH_RETRY_DELAYS_MS[attempt]);
        attempt += 1;
        if (seq !== seqRef.current) return; // superseded while waiting
        continue;
      }

      let agents = [];
      let invites = [];
      let roles = [];

      if (agentsResult.status === "fulfilled") {
        agents = (agentsResult.value.agents ?? []).map((a) => ({ ...a, _type: "agent" }));
      } else if (!isSilentDirectoryBlip(agentsResult.reason)) {
        // Only surface genuine (non-transient) failures.
        setError(
          agentsResult.reason instanceof Error
            ? agentsResult.reason
            : new Error(String(agentsResult.reason))
        );
      } else if (typeof console !== "undefined" && console.debug) {
        console.debug("[users] suppressed agents load blip", agentsResult.reason);
      }

      if (invitesResult.status === "fulfilled") {
        invites = (invitesResult.value.invitations ?? []).map((inv) => ({
          ...inv,
          _type: "pending",
        }));
      } else if (!isSilentDirectoryBlip(invitesResult.reason)) {
        setInviteError(
          invitesResult.reason instanceof Error
            ? invitesResult.reason
            : new Error(String(invitesResult.reason))
        );
      } else if (typeof console !== "undefined" && console.debug) {
        console.debug("[users] suppressed invitations load blip", invitesResult.reason);
      }

      if (rolesResult.status === "fulfilled") {
        roles = (rolesResult.value.roles ?? [])
          .map((role) => ({
            value: String(role.key ?? "").trim(),
            label: String(role.label ?? role.key ?? "").trim(),
            isSystem: Boolean(role.isSystem),
          }))
          .filter((role) => role.value);
      } else if (!isSilentDirectoryBlip(rolesResult.reason)) {
        setRoleError(
          rolesResult.reason instanceof Error
            ? rolesResult.reason
            : new Error(String(rolesResult.reason))
        );
      } else if (typeof console !== "undefined" && console.debug) {
        console.debug("[users] suppressed roles load blip", rolesResult.reason);
      }

      const agentsFulfilled = agentsResult.status === "fulfilled";
      const invitesFulfilled = invitesResult.status === "fulfilled";

      if (agentsFulfilled || invitesFulfilled) {
        setRows((prev) => {
          const prevAgents = prev.filter((r) => r._type === "agent");
          const prevInvites = prev.filter((r) => r._type === "pending");
          const agentRows = agentsFulfilled
            ? agents.sort((a, b) => {
                const activeDiff = Number(Boolean(b.isActive)) - Number(Boolean(a.isActive));
                if (activeDiff !== 0) return activeDiff;
                return String(a.agentName ?? "").localeCompare(String(b.agentName ?? ""));
              })
            : prevAgents;
          const inviteRows = invitesFulfilled ? invites : prevInvites;
          return [...agentRows, ...inviteRows];
        });
      }
      if (agentsFulfilled) {
        setTotal(agents.length);
      }
      if (rolesResult.status === "fulfilled") {
        setRoleOptions(roles);
      }
      setLoading(false);
      return;
    }
  }, [activeWorkspaceId, directoryScope]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Presence is written by human-agent-service; the directory snapshot is otherwise
  // one-shot. Poll + live events keep "Active — accepting chats" in sync.
  useEffect(() => {
    if (!activeWorkspaceId) return undefined;

    const pullPresence = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const data = await listWorkspaceAgents({ limit: 100, offset: 0, scope: directoryScope });
        const agents = data.agents ?? [];
        setRows((prev) => {
          const byEmail = new Map(
            agents.map((a) => [String(a.email || "").trim().toLowerCase(), a]),
          );
          let changed = false;
          const next = prev.map((r) => {
            if (r._type !== "agent") return r;
            const fresh = byEmail.get(String(r.email || "").trim().toLowerCase());
            if (!fresh) return r;
            if (
              Boolean(r.isOnline) === Boolean(fresh.isOnline) &&
              r.lastLogin === fresh.lastLogin &&
              r.updatedAt === fresh.updatedAt
            ) {
              return r;
            }
            changed = true;
            return {
              ...r,
              isOnline: Boolean(fresh.isOnline),
              lastLogin: fresh.lastLogin,
              updatedAt: fresh.updatedAt,
            };
          });
          return changed ? next : prev;
        });
      } catch {
        /* next tick retries */
      }
    };

    const timerId = setInterval(pullPresence, PRESENCE_POLL_MS);
    document.addEventListener("visibilitychange", pullPresence);
    const unsubscribe = subscribeAgentStatusChanged((payload) => {
      setRows((prev) => patchAgentPresence(prev, payload));
    });

    return () => {
      clearInterval(timerId);
      document.removeEventListener("visibilitychange", pullPresence);
      unsubscribe();
    };
  }, [activeWorkspaceId, directoryScope]);

  return {
    rows,
    setRows,
    total,
    loading,
    error,
    inviteError,
    roleError,
    roleOptions,
    refresh,
    tenantWide,
    directoryScope,
  };
}
